import React from 'react';
import { useTranslate } from 'react-admin';
import { Box, MenuItem, TextField } from '@mui/material';
import type { KycOperationType } from '@/types';
import { OCR_OPERATION_TYPES } from '@/config/constants';
import { useOcrLabels } from './ocrLabels';

export interface CaseInfo {
  name: string;
  description: string;
  documentTypeName: string;
  nationalityCode: string;
  operationType: KycOperationType;
}

/** The descriptive fields of a test case — the same on the create page and on the case page. */
export const CaseInfoFields: React.FC<{ value: CaseInfo; onChange: (patch: Partial<CaseInfo>) => void }> = ({
  value, onChange,
}) => {
  const t = useTranslate();
  const labels = useOcrLabels();

  return (
    // A grid rather than FieldPair: these are plain MUI fields with no react-admin helper-text margin,
    // so the rows need a gap of their own when the pairs stack on a phone.
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' } }}>
      <TextField
        fullWidth
        required
        label={t('screens.ocrTesting.name')}
        value={value.name}
        onChange={(e) => onChange({ name: e.target.value })}
      />
      <TextField
        select
        fullWidth
        label={t('screens.ocrTesting.operationType')}
        value={value.operationType}
        onChange={(e) => onChange({ operationType: Number(e.target.value) as KycOperationType })}
      >
        {OCR_OPERATION_TYPES.map((op) => (
          <MenuItem key={op.id} value={op.id}>{labels.operationType(op.id)}</MenuItem>
        ))}
      </TextField>
      <TextField
        fullWidth
        label={t('screens.ocrTesting.documentType')}
        value={value.documentTypeName}
        onChange={(e) => onChange({ documentTypeName: e.target.value })}
      />
      <TextField
        fullWidth
        label={t('screens.ocrTesting.nationality')}
        value={value.nationalityCode}
        onChange={(e) => onChange({ nationalityCode: e.target.value })}
        inputProps={{ maxLength: 3 }}
        helperText={t('screens.ocrTesting.nationalityHelp')}
      />
      <TextField
        fullWidth
        multiline
        rows={2}
        label={t('screens.ocrTesting.description')}
        value={value.description}
        onChange={(e) => onChange({ description: e.target.value })}
        sx={{ gridColumn: '1 / -1' }}
      />
    </Box>
  );
};
